import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CalendarDays, Clock, Users } from "lucide-react";
import { apiService } from "@/services/api";
import { LeaveBalance, LeaveBalanceSummary } from "@/types";
import { useAuth } from "@/contexts/AuthContext";

export const LeaveBalanceCard = () => {
  const { user } = useAuth();
  const [leaveBalance, setLeaveBalance] = useState<LeaveBalance | null>(null);
  const [summary, setSummary] = useState<LeaveBalanceSummary | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadLeaveData();
  }, []);

  const loadLeaveData = async () => {
    try {
      setLoading(true);
      
      // Load employee's leave balance if user is an employee
      if (user?.role === 'employee' && user?.employeeId) {
        const balanceResponse = await apiService.getEmployeeLeaveBalance(user.employeeId);
        if (balanceResponse.data) {
          setLeaveBalance(balanceResponse.data);
        }
      }
      
      // Load summary for all users (council-scoped)
      const summaryResponse = await apiService.getLeaveBalanceSummary(user?.cid ?? null);
      if (summaryResponse.data) {
        setSummary(summaryResponse.data);
      }
    } catch (error) {
      console.error('Error loading leave data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CalendarDays className="h-5 w-5" />
            Leave Balance
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center text-gray-500">Loading...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CalendarDays className="h-5 w-5" />
          Leave Balance
        </CardTitle>
        <CardDescription>
          {user?.role === 'employee' 
            ? 'Your current leave balance' 
            : 'Leave balance overview'
          }
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {user?.role === 'employee' && leaveBalance ? (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Available Days</span>
              <Badge 
                variant={leaveBalance.leave_days_balance < 5 ? "destructive" : "default"}
                className="text-lg px-3 py-1"
              >
                {leaveBalance.leave_days_balance.toFixed(1)} days
              </Badge>
            </div>
            
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-green-600" />
                <span>Accrued: {leaveBalance.leave_days_accrued.toFixed(1)}</span>
              </div>
              <div className="flex items-center gap-2">
                <CalendarDays className="h-4 w-4 text-blue-600" />
                <span>Used: {leaveBalance.leave_days_used.toFixed(1)}</span>
              </div>
            </div>
            
            {leaveBalance.scale_name && (
              <div className="text-xs text-gray-500">
                Scale: {leaveBalance.scale_name} 
                ({leaveBalance.annual_leave_days} days/month)
              </div>
            )}
          </div>
        ) : summary ? (
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <div className="text-2xl font-bold text-blue-600">
                  {summary.total_employees}
                </div>
                <div className="text-xs text-gray-500">Total Employees</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-green-600">
                  {summary.average_balance.toFixed(1)}
                </div>
                <div className="text-xs text-gray-500">Avg Balance</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-orange-600">
                  {summary.low_balance_count}
                </div>
                <div className="text-xs text-gray-500">Low Balance</div>
              </div>
            </div>
            
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Users className="h-4 w-4" />
              <span>
                {summary.low_balance_count} employees have less than 5 days leave
              </span>
            </div>
          </div>
        ) : (
          <div className="text-center text-gray-500">
            No leave data available
          </div>
        )}
      </CardContent>
    </Card>
  );
};

